/**
 * PublicSiteHeader — the sterilemate.com public top menu (logos + nav + cart + CTAs).
 *
 * Designed to be embedded INSIDE other layouts (e.g. the Hospital Dashboard
 * Home tab), so it uses `sticky top-0` instead of `fixed`. That keeps it
 * inside the main content column and never overlaps the persistent sidebar.
 *
 * If you ever need the full-bleed fixed version, the LandingPage retains its
 * own inlined copy.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  Menu,
  X,
  ShoppingCart,
  ChevronDown,
  Leaf,
  Activity,
  Stethoscope,
  Package,
  Droplet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteCart } from "@/contexts/QuoteCartContext";

const ASKISAAC_LOGO =
  "/logos/askisaac-b-monogram.svg";
const STERILEMATE_LOGO =
  "/logos/sterilemate-white.png?v=4";

const SOLM_CATEGORIES = [
  ["diabetes-care", "Diabetes Care"],
  ["medication-delivery", "Medication Delivery"],
  ["blood-collection", "Blood Collection"],
  ["wound-care", "Wound Care"],
  ["ppe", "PPE"],
  ["vascular-access", "Vascular Access & Infusion"],
  ["incontinence", "Incontinence"],
  ["bulk-non-sterile", "Bulk Non-Sterile"],
];

const MobileSection = ({ id, label, open, onToggle, children, testid }) => (
  <div className="rounded-lg overflow-hidden">
    <button
      type="button"
      onClick={() => onToggle(id)}
      className={`w-full flex items-center justify-between px-3 py-3 rounded-lg text-slate-200 hover:bg-slate-800/60 transition-colors ${
        open ? "bg-slate-800/60" : ""
      }`}
      aria-expanded={open}
      data-testid={testid}
    >
      <span className="font-medium">{label}</span>
      <ChevronDown
        className={`w-4 h-4 text-slate-400 transition-transform ${
          open ? "rotate-180" : ""
        }`}
      />
    </button>
    {open && (
      <div
        className="pl-3 pr-1 py-1 ml-3 border-l border-slate-800 space-y-0.5"
        data-testid={`${testid}-panel`}
      >
        {children}
      </div>
    )}
  </div>
);

export default function PublicSiteHeader({ showCTAs = true }) {
  const { cart: quoteCart, setShowCart: openQuoteCart } = useQuoteCart();
  const quoteCartCount = quoteCart.reduce((s, i) => s + (i.qty || 0), 0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);

  const toggleMobileSection = (id) =>
    setMobileSection((cur) => (cur === id ? null : id));

  return (
    <>
      <header
        className="sticky top-0 z-40 shadow-lg shadow-black/30"
        data-testid="public-site-header"
        style={{
          background:
            "linear-gradient(90deg, #050a16 0%, #0b1e3f 32%, #123a75 65%, #0056A8 100%)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* Left dark fade — keeps logos crisp */}
        <div
          aria-hidden
          className="absolute inset-y-0 left-0 pointer-events-none w-[440px] max-w-full"
          style={{
            background:
              "linear-gradient(90deg, rgba(5,10,22,0.85) 0%, rgba(5,10,22,0.55) 45%, rgba(5,10,22,0) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-6 h-16">
          <Link
            to="/"
            className="flex items-center gap-3 shrink-0"
            data-testid="public-header-logo"
          >
            <img
              src={STERILEMATE_LOGO}
              alt="Sterile Mate"
              className="h-10 w-auto"
             
            />
            <span className="hidden sm:inline text-white/70 font-medium italic tracking-wide text-xs">
              powered by
            </span>
            <img
              src={ASKISAAC_LOGO}
              alt="AskIsaac"
              className="hidden sm:block h-7 w-auto"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
            {/* How It Works dropdown */}
            <div className="relative group" data-testid="public-header-howitworks-dropdown">
              <a
                href="#how-it-works"
                className="text-sm font-medium text-white/85 hover:text-white transition-colors whitespace-nowrap flex items-center gap-1"
              >
                How It Works
                <ChevronDown className="w-3 h-3 mt-0.5" />
              </a>
              <div className="absolute left-0 top-full pt-2 w-[340px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="bg-[#0b1d3a] border border-slate-700 rounded-xl shadow-2xl overflow-hidden">
                  <Link
                    to="/how-it-works"
                    className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800"
                  >
                    <p className="text-sm font-semibold text-white">Platform Overview</p>
                    <p className="text-xs text-slate-400">
                      QuickBid Exchange · 6-step explainer · how AskIsaac works
                    </p>
                  </Link>
                  <Link
                    to="/how-it-works/backorder-sourcing"
                    className="block px-4 py-3 hover:bg-slate-800/60"
                  >
                    <p className="text-sm font-semibold text-white">Backorder Sourcing</p>
                    <p className="text-xs text-slate-400">
                      How we hunt allocated SKUs on your behalf
                    </p>
                  </Link>
                </div>
              </div>
            </div>

            <a
              href="/#quick-quote"
              className="text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors whitespace-nowrap"
              data-testid="public-header-backorders"
            >
              Backorders
            </a>
            <Link
              to="/solutions"
              className="text-sm font-medium text-white/85 hover:text-white transition-colors"
            >
              Solutions
            </Link>

            <Link
              to="/join-network"
              className="text-sm font-medium text-white/85 hover:text-white transition-colors whitespace-nowrap"
            >
              Join Network
            </Link>

            <Link
              to="/about"
              className="text-sm font-medium text-white/85 hover:text-white transition-colors whitespace-nowrap"
              data-testid="public-header-about-link"
            >
              About
            </Link>

            {/* Marketplace mega-menu (Marketplace + merged Surgical/Sol-M/ENT) */}
            <div className="relative group" data-testid="public-header-marketplace-dropdown">
              <Link
                to="/marketplace"
                className="text-sm font-medium text-white/85 hover:text-white transition-colors flex items-center gap-1"
              >
                Marketplace
                <ChevronDown className="w-3 h-3 mt-0.5" />
              </Link>
              <div className="absolute right-0 top-full pt-2 w-[820px] max-w-[95vw] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="bg-[#0b1d3a] border border-slate-700 rounded-xl shadow-2xl overflow-hidden">
                  <div className="grid grid-cols-2">
                    {/* Left column: Marketplace categories */}
                    <div className="border-r border-slate-800 max-h-[75vh] overflow-y-auto">
                      <div className="px-4 py-2 border-b border-slate-800">
                        <p className="text-[10px] font-semibold text-cyan-300 uppercase tracking-wider">Marketplace</p>
                      </div>
                      <Link to="/marketplace" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800">
                        <p className="text-sm font-semibold text-white">All Marketplace Products</p>
                        <p className="text-xs text-slate-400">Full catalog · every category</p>
                      </Link>
                      <Link to="/backorders/live-inventory" className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-800/60 border-b border-slate-800" data-testid="public-header-live-inventory-link">
                        <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center shrink-0"><Package className="w-4 h-4 text-slate-900" /></div>
                        <div><p className="text-sm font-semibold text-white">Weekly Deals / Backorder Items <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-500 text-slate-900 font-bold">LIVE</span></p><p className="text-xs text-slate-400">Live hard-to-find inventory · search any time</p></div>
                      </Link>
                      <Link to="/marketplace?category=evs-janitorial" className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-800/60 border-b border-slate-800">
                        <div className="w-8 h-8 rounded-lg bg-emerald-600/80 flex items-center justify-center shrink-0"><Leaf className="w-4 h-4 text-white" /></div>
                        <div><p className="text-sm font-semibold text-white">Janitorial &amp; EVS</p><p className="text-xs text-slate-400">Microfiber · Filmop · chemicals</p></div>
                      </Link>
                      <Link to="/marketplace?category=home-health-aids" className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-800/60 border-b border-slate-800">
                        <div className="w-8 h-8 rounded-lg bg-rose-500/80 flex items-center justify-center shrink-0"><Activity className="w-4 h-4 text-white" /></div>
                        <div><p className="text-sm font-semibold text-white">Home Health Care Aids</p><p className="text-xs text-slate-400">BP monitors · diabetes · daily-living aids</p></div>
                      </Link>
                      <Link to="/solutions/diabetes-management" className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-800/60 border-b border-slate-800" data-testid="public-header-diabetes-link">
                        <div className="w-8 h-8 rounded-lg bg-sky-500/80 flex items-center justify-center shrink-0"><Droplet className="w-4 h-4 text-white" /></div>
                        <div><p className="text-sm font-semibold text-white">Diabetes Management <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-sky-500 text-slate-900 font-bold">NEW</span></p><p className="text-xs text-slate-400">Glucose monitors · test strips · lancets</p></div>
                      </Link>
                      <Link to="/marketplace?category=blood-pressure-monitors" className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-800/60 border-b border-slate-800" data-testid="public-header-bp-monitors-link">
                        <div className="w-8 h-8 rounded-lg bg-red-600/80 flex items-center justify-center shrink-0"><Stethoscope className="w-4 h-4 text-white" /></div>
                        <div><p className="text-sm font-semibold text-white">Blood Pressure Monitors</p><p className="text-xs text-slate-400">Automatic · aneroid · ambulatory · pediatric cuffs</p></div>
                      </Link>
                      <Link to="/marketplace?category=filmop-usa" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800">
                        <p className="text-sm font-semibold text-white">Filmop USA</p>
                        <p className="text-xs text-slate-400">Alpha trolleys · frames · poles</p>
                      </Link>
                      <Link to="/marketplace/medical-devices" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800">
                        <p className="text-sm font-semibold text-white">Medical Devices</p>
                        <p className="text-xs text-slate-400">BP Monitors · Blood Collection · Needles &amp; Syringes</p>
                      </Link>
                      <Link to="/marketplace/medical-devices/vive-power-mobility" className="block pl-8 pr-4 py-2 hover:bg-slate-800/60 border-b border-slate-800 bg-slate-900/40"><p className="text-xs font-semibold text-slate-200">— Power Mobility</p></Link>
                      <Link to="/marketplace/medical-devices/vive-mobility" className="block pl-8 pr-4 py-2 hover:bg-slate-800/60 border-b border-slate-800 bg-slate-900/40"><p className="text-xs font-semibold text-slate-200">— Mobility</p></Link>
                      <Link to="/marketplace/medical-devices/vive-bracing" className="block pl-8 pr-4 py-2 hover:bg-slate-800/60 border-b border-slate-800 bg-slate-900/40"><p className="text-xs font-semibold text-slate-200">— Bracing</p></Link>
                      <Link to="/marketplace/medical-devices/vive-health-devices" className="block pl-8 pr-4 py-2 hover:bg-slate-800/60 border-b border-slate-800 bg-slate-900/40"><p className="text-xs font-semibold text-slate-200">— Health Devices</p></Link>
                      <Link to="/marketplace/medical-devices/vive-bathroom" className="block pl-8 pr-4 py-2 hover:bg-slate-800/60 border-b border-slate-800 bg-slate-900/40"><p className="text-xs font-semibold text-slate-200">— Bathroom</p></Link>
                      <Link to="/marketplace/medical-devices/vive-bedroom" className="block pl-8 pr-4 py-2 hover:bg-slate-800/60 border-b border-slate-800 bg-slate-900/40"><p className="text-xs font-semibold text-slate-200">— Bedroom</p></Link>
                      <Link to="/marketplace/medical-devices/vive-living-aids" className="block pl-8 pr-4 py-2 hover:bg-slate-800/60 border-b border-slate-800 bg-slate-900/40"><p className="text-xs font-semibold text-slate-200">— Living Aids</p></Link>
                      <Link to="/marketplace/medical-devices/vive-footcare" className="block pl-8 pr-4 py-2 hover:bg-slate-800/60 border-b border-slate-800 bg-slate-900/40"><p className="text-xs font-semibold text-slate-200">— Footcare</p></Link>
                      <Link to="/marketplace/medical-devices/vive-physical-therapy" className="block pl-8 pr-4 py-2 hover:bg-slate-800/60 border-b border-slate-800 bg-slate-900/40"><p className="text-xs font-semibold text-slate-200">— Physical Therapy</p></Link>
                      <Link to="/marketplace/di-tank-exchange" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800">
                        <p className="text-sm font-semibold text-white">DI Tank Exchange</p>
                        <p className="text-xs text-slate-400">Next-day exchange · No contracts · cGMP / ISO 9001</p>
                      </Link>
                      <Link to="/dialysis" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800" data-testid="public-header-hemodialysis-link">
                        <p className="text-sm font-semibold text-white">Hemodialysis Product &amp; Care <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-teal-500/30 text-teal-200 font-bold">NEW</span></p>
                        <p className="text-xs text-slate-400">Water · concentrates · additives · equipment · compliance</p>
                      </Link>
                      <Link to="/sterile-processing" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800" data-testid="public-header-sterile-processing-link">
                        <p className="text-sm font-semibold text-white">Sterile Processing &mdash; MicroCare <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-fuchsia-500/30 text-fuchsia-200 font-bold">NEW</span></p>
                        <p className="text-xs text-slate-400">ProEZ · ProChek · ProSpray · SPD compliance</p>
                      </Link>
                      <Link to="/marketplace?category=water-purification" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800" data-testid="public-header-water-purification-link">
                        <p className="text-sm font-semibold text-white">Healthcare Water Purification Systems <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-sky-500/30 text-sky-200 font-bold">ST108</span></p>
                        <p className="text-xs text-slate-400">Hydronix ROS-IXC · softeners · monitoring · RO/DI</p>
                      </Link>
                      <Link to="/marketplace/green-klean" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800">
                        <p className="text-sm font-semibold text-white">Green Klean®</p>
                        <p className="text-xs text-slate-400">Chlorinated Disinfecting Tablets · EPA #71847-6-91038</p>
                      </Link>
                      <Link to="/microbiology-testing" className="block px-4 py-3 hover:bg-slate-800/60">
                        <p className="text-sm font-semibold text-white">Microbiology Testing</p>
                        <p className="text-xs text-slate-400">A&amp;B Labs · ISO 17025 · Water + bio-burden panels</p>
                      </Link>
                    </div>
                    {/* Right column: Surgical + Sol-M + ENT */}
                    <div className="max-h-[75vh] overflow-y-auto">
                      <div className="px-4 py-2 border-b border-slate-800">
                        <p className="text-[10px] font-semibold text-amber-300 uppercase tracking-wider">Surgical &amp; Clinical</p>
                      </div>
                      <Link to="/sterilemate/surgical" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800">
                        <p className="text-sm font-semibold text-white">Surgical Marketplace</p>
                        <p className="text-xs text-slate-400">2,000+ SKUs · Aspen Medical, BluBox</p>
                      </Link>
                      <Link to="/marketplace/american-surgical" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800">
                        <p className="text-sm font-semibold text-white">Neuro Patty Sponges</p>
                        <p className="text-xs text-slate-400">American Surgical · 124 SKUs · Salem, MA</p>
                      </Link>
                      <Link to="/sterilemate/ent-surgical" className="block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800">
                        <p className="text-sm font-semibold text-white">ENT — BEAR-ENT</p>
                        <p className="text-xs text-slate-400">93+ SKUs · Ear · Nose · Throat + Sets</p>
                      </Link>
                      <div className="px-4 py-2 border-b border-slate-800">
                        <p className="text-[10px] font-semibold text-cyan-300 uppercase tracking-wider">Sol-M Authorized Distributor</p>
                      </div>
                      {SOLM_CATEGORIES.map(([slug, name]) => (
                        <Link
                          key={slug}
                          to={`/sterilemate/${slug}`}
                          className="block px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800/60"
                        >
                          {name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-3 shrink-0">
            <a
              href="tel:2814563458"
              className="hidden 2xl:flex text-sm font-medium text-white/70 hover:text-white transition-colors items-center gap-1 whitespace-nowrap"
            >
              <Phone className="w-3 h-3" />
              281.456.3458
            </a>

            <button
              type="button"
              onClick={() => openQuoteCart(true)}
              className="relative inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white text-sm font-semibold whitespace-nowrap transition-colors backdrop-blur-sm"
              data-testid="public-header-cart-btn"
              aria-label={`Open quote cart (${quoteCartCount} items)`}
            >
              <ShoppingCart className="w-4 h-4 text-amber-400" />
              <span className="hidden sm:inline">Cart</span>
              {quoteCartCount > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[20px] h-[20px] rounded-full bg-amber-500 text-slate-900 text-[10px] font-black px-1.5"
                  data-testid="public-header-cart-count"
                >
                  {quoteCartCount}
                </span>
              )}
            </button>

            {showCTAs && (
              <Button
                asChild
                className="hidden sm:inline-flex bg-white text-[#003366] hover:bg-white/90 rounded-lg px-4 xl:px-5 h-9 text-sm font-semibold whitespace-nowrap shadow-md"
                data-testid="public-header-get-started-btn"
              >
                <Link to="/marketplace">Browse Marketplace</Link>
              </Button>
            )}

            <button
              onClick={() => {
                setMobileMenuOpen(true);
                setMobileSection(null);
              }}
              aria-label="Open menu"
              className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-md text-white/90 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="public-header-mobile-toggle"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="public-header-mobile-drawer">
          <button
            aria-label="Close menu backdrop"
            onClick={() => setMobileMenuOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          <div className="absolute right-0 top-0 bottom-0 w-[88%] max-w-sm bg-[#0a0f1a] border-l border-slate-800 shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-5 h-14 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <img
                  src={STERILEMATE_LOGO}
                  alt="Sterile Mate"
                  className="h-6 w-auto"
                 
                />
                <div className="w-px h-5 bg-slate-700" />
                <img src={ASKISAAC_LOGO} alt="AskIsaac" className="h-5 w-auto" />
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
                className="w-10 h-10 inline-flex items-center justify-center rounded-md text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                data-testid="public-header-mobile-close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <nav className="px-3 py-4 space-y-1">
              <MobileSection
                id="howitworks"
                label="How It Works"
                open={mobileSection === "howitworks"}
                onToggle={toggleMobileSection}
                testid="public-mobile-howitworks"
              >
                <Link
                  to="/how-it-works"
                  className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Platform Overview
                </Link>
                <Link
                  to="/how-it-works/backorder-sourcing"
                  className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Backorder Sourcing
                </Link>
              </MobileSection>

              <a
                href="/#quick-quote"
                className="block px-3 py-3 rounded-lg text-amber-400 hover:bg-slate-800/60"
                onClick={() => setMobileMenuOpen(false)}
              >
                Backorders
              </a>
              <Link
                to="/solutions"
                className="block px-3 py-3 rounded-lg text-slate-200 hover:bg-slate-800/60"
                onClick={() => setMobileMenuOpen(false)}
              >
                Solutions
              </Link>

              <Link
                to="/join-network"
                className="block px-3 py-3 rounded-lg text-slate-200 hover:bg-slate-800/60"
                onClick={() => setMobileMenuOpen(false)}
              >
                Join Network
              </Link>

              <Link
                to="/about"
                className="block px-3 py-3 rounded-lg text-slate-200 hover:bg-slate-800/60"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="public-mobile-about-link"
              >
                About
              </Link>

              <MobileSection
                id="marketplace"
                label="Marketplace"
                open={mobileSection === "marketplace"}
                onToggle={toggleMobileSection}
                testid="public-mobile-marketplace"
              >
                <Link to="/marketplace" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)}>All Marketplace Products</Link>
                <Link to="/marketplace/janitorial" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)}>Janitorial / EVS</Link>
                <Link to="/marketplace?category=home-health-aids" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)}>Home Health Care Aids</Link>
                <Link to="/solutions/diabetes-management" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)} data-testid="public-mobile-diabetes">Diabetes Management</Link>
                <Link to="/marketplace?category=blood-pressure-monitors" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)} data-testid="public-mobile-bp-monitors">Blood Pressure Monitors</Link>
                <Link to="/marketplace?category=filmop-usa" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)}>Filmop USA</Link>
                <Link to="/marketplace/medical-devices" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)}>Medical Devices</Link>
                <Link to="/marketplace/di-tank-exchange" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)}>DI Tank Exchange</Link>
                <Link to="/dialysis" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)} data-testid="public-mobile-hemodialysis">Hemodialysis Product &amp; Care</Link>
                <Link to="/sterile-processing" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)} data-testid="public-mobile-sterile-processing">Sterile Processing — MicroCare</Link>
                <Link to="/marketplace?category=water-purification" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)} data-testid="public-mobile-water-purification">Healthcare Water Purification</Link>
                <Link to="/marketplace/green-klean" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)}>Green Klean®</Link>
                <Link to="/microbiology-testing" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)}>Microbiology Testing</Link>
                <div className="px-3 pt-3 pb-1 text-[10px] font-semibold text-amber-300 uppercase tracking-wider">Surgical &amp; Clinical</div>
                <Link to="/sterilemate/surgical" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)}>Surgical Marketplace</Link>
                <Link to="/marketplace/american-surgical" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)}>Neuro Patty Sponges</Link>
                <Link to="/sterilemate/ent-surgical" className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60" onClick={() => setMobileMenuOpen(false)}>ENT — BEAR-ENT</Link>
                <div className="px-3 pt-3 pb-1 text-[10px] font-semibold text-cyan-300 uppercase tracking-wider">Sol-M Authorized Distributor</div>
                {SOLM_CATEGORIES.map(([slug, name]) => (
                  <Link
                    key={slug}
                    to={`/sterilemate/${slug}`}
                    className="block px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800/60"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {name}
                  </Link>
                ))}
              </MobileSection>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
